import React, { useEffect, useRef, useState } from "react";
import "./Chats.css";
import { VscSend } from "react-icons/vsc";
import Loader from "../../Loader/Loader";

import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { connectChatSocket} from "../../socket/chatSocket";
import { getChatMessages, sendMessageApi } from "../../utils/APIs/chatApis";
import AddQuoteModal from "./AddQuoteModal/AddQuoteModal";
import { useChatData } from "../../Context/chatDataContext";
import { toast } from "react-toastify";
import QuotePaymentCard from "./QuotePaymentCard/QuotePaymentCard";
import QuoteSummaryCard, { getQuoteSummaryDataFromMessage } from "./QuoteSummaryCard/QuoteSummaryCard";
import AddBookingModal from "./AddBookingModal/AddBookingModal";
import QuotePinnedCard from "./QuotePinnedCard/QuotePinnedCard";
import BookingPinnedCard from "./BookingPinnedCard/BookingPinnedCard";

const Chats = ({ chatType }) => {
  const { bookingId, quoteId } = useParams();
  const targetId = chatType === "booking" ? bookingId : quoteId;
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const { quote, booking } = useChatData();
  const { toggleSidebar } = useOutletContext() || {};

  useEffect(() => {
    if (chatType === "quote" && !quote && quoteId) {
      // fallback fetch
    }
    if (chatType === "booking" && !booking && bookingId) {
      // fallback fetch
    }
  }, [chatType, quoteId, bookingId, quote, booking]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const chatEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [quotePinnedMessages, setQuotePinnedMessages] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messageForName, setMessageForName] = useState(null);
  const oppontentName = messages.find((singleMessage) => singleMessage.senderId !== null);
  const navigate = useNavigate();

  const clientAvatar =
    messageForName?.pinned?.client_id?.avatar ||
    messageForName?.pinned?.clientId?.avatar;
  const clientName =
    messageForName?.userName ||
    messageForName?.pinned?.clientName ||
    messageForName?.pinned?.client_id?.username ||
    messageForName?.pinned?.clientId?.username ||
    oppontentName?.senderId?.username ||
    "User";

  useEffect(() => {
    const fetchMessages = async () => {
      if (!targetId) return;
      try {
        setLoading(true);
        const res = await getChatMessages(targetId);
        if (res.data?.success) {
          setQuotePinnedMessages(res?.data?.pinned);
          setMessages(res?.data?.data);
          setMessageForName(res?.data);
        }
      } catch (err) {
        console.error("Failed to load messages", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [targetId]);

  useEffect(() => {
    if (!targetId) return;

    const socket = connectChatSocket();
    socket.emit("join_booking_chat", { bookingId: targetId });

    const handleReceiveMessage = (newMessage) => {
      setMessages((prev) => {
        const exists = prev.some((m) => m._id === newMessage._id);
        if (exists) return prev;
        return [...prev, newMessage];
      });
    };

    socket.on("receive_message", handleReceiveMessage);

    return () => {
      socket.off("receive_message", handleReceiveMessage);
    };
  }, [targetId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || !targetId) return;

    const messageText = input;
    setInput("");

    try {
      const res = await sendMessageApi(targetId, messageText);
      if (!res.data?.success) {
        console.error("Message send failed");
      }
    } catch (err) {
      console.error("Message send failed", err);
    }
  };

  const handleQuoteSend = async (payload) => {
    if (!targetId) return;
    try {
      const res = await sendMessageApi(targetId, payload);
      if (res?.data?.success) {
        toast.success("Budget sent successfully!");
        setShowPaymentModal(false);
      } else {
        toast.error(res?.data?.message || "Failed to send budget.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to send budget.");
    }
  };

  const handleBookingSend = () => {
    console.log("BOOKING SEND");
  };

  if (loading && messages.length === 0) return <Loader />;

  return (
    <div className="admin-chat-page">
      <div className="chat-wrapper chat-wrapper--admin-full">
        <header className="chat-header">
          <div className="chat-header-left">
            {toggleSidebar && (
              <button
                type="button"
                className="chat-sidebar-toggle chat-sidebar-toggle--mobile"
                onClick={toggleSidebar}
                aria-label="Open menu"
              >
                ☰
              </button>
            )}
            <button
              type="button"
              className="chat-back-btn"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              ←
            </button>
            <img
              src={clientAvatar || "/ProjectLogo/WhiteLogo.png"}
              alt={clientName}
              className="chat-header-avatar"
            />
            <div className="admin-info">
              <p className="admin-name mb-0">{clientName}</p>
            </div>
          </div>

          <div className="chat-header-right">
            {!bookingId && (
              <button
                type="button"
                className="chat-header-btn"
                onClick={() => {
                  if (quoteId) setShowPaymentModal(true);
                  if (bookingId) setShowBookingModal(true);
                }}
              >
                +
              </button>
            )}
          </div>
        </header>

        {quoteId && (
          <AddQuoteModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onSubmit={handleQuoteSend}
            queryData={quote}
          />
        )}
        {bookingId && (
          <AddBookingModal
            isOpen={showBookingModal}
            onClose={() => setShowBookingModal(false)}
            onSubmit={handleBookingSend}
          />
        )}

        <div className="chat-main">
          <div className="chat-pinned-bar">
            {chatType === "quote" && (
              <QuotePinnedCard pinned={quotePinnedMessages} formatDate={formatDate} />
            )}
            {chatType === "booking" && (
              <BookingPinnedCard pinned={quotePinnedMessages} />
            )}
          </div>

          <div className="chat-body">
            {messages.length === 0 && !loading && (
              <div className="text-center text-muted mt-5">
                No messages yet. Start the conversation!
              </div>
            )}
            {messages.map((msg) => {
              const sender = msg.senderId;
              const isMe = msg?.senderId === null;

              return (
                <div
                  key={msg._id || Math.random()}
                  className={`message-row ${isMe ? "right" : "left"}`}
                >
                  {!isMe && (
                    <img
                      src={sender?.avatar || "https://i.pravatar.cc/40?img=12"}
                      alt="avatar"
                      className="avatar"
                      onError={(e) => {
                        e.target.src = "https://i.pravatar.cc/40?img=12";
                      }}
                    />
                  )}

                  <div className={`message-bubble ${isMe ? "dark" : "light"}`}>
                    <small className="sender-name">
                      {isMe ? "You" : sender?.username || "User"}
                    </small>
                    <div className="text-content">
                      {(() => {
                        const summaryData = getQuoteSummaryDataFromMessage(msg);
                        if (summaryData) {
                          return <QuoteSummaryCard data={summaryData} compact />;
                        }
                        return null;
                      })()}

                      {msg.messageType === "text" &&
                        !getQuoteSummaryDataFromMessage(msg) &&
                        msg.message}

                      {msg.messageType === "paymentCard" && quoteId && (
                        <QuotePaymentCard
                          msg={msg}
                          isMe={isMe}
                          formatDate={formatDate}
                          formatTime={formatTime}
                        />
                      )}
                    </div>

                    <span className="time">
                      {new Date(msg.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>

          <div className="chat-input">
            <div className="chat-input-inner">
              <input
                type="text"
                placeholder="Start Typing..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
              />
              <button
                type="button"
                className={`send-btn ${input.trim() ? "active" : ""}`}
                onClick={handleSend}
                disabled={!input.trim()}
              >
                <VscSend />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chats;
